import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type EventHandlingContextValue = {
  muteChangeCounter: number;
  notifyMuteChanged: () => void;
  seenChangeCounter: number;
  notifySeenChanged: () => void;
};

const EventHandlingContext = createContext<
  EventHandlingContextValue | undefined
>(undefined);

type EventHandlingProviderProps = {
  children: ReactNode;
};

export const EventHandlingProvider: FC<EventHandlingProviderProps> = ({
  children,
}) => {
  const [muteChangeCounter, setMuteChangeCounter] = useState(0);
  const [seenChangeCounter, setSeenChangeCounter] = useState(0);

  const notifyMuteChanged = useCallback(() => {
    setMuteChangeCounter((prev) => prev + 1);
  }, []);

  const notifySeenChanged = useCallback(() => {
    setSeenChangeCounter((prev) => prev + 1);
  }, []);

  const value = useMemo(
    () => ({
      muteChangeCounter,
      notifyMuteChanged,
      seenChangeCounter,
      notifySeenChanged,
    }),
    [
      muteChangeCounter,
      notifyMuteChanged,
      seenChangeCounter,
      notifySeenChanged,
    ],
  );

  return (
    <EventHandlingContext.Provider value={value}>
      {children}
    </EventHandlingContext.Provider>
  );
};

export const useEventHandlingContext = (): EventHandlingContextValue => {
  const ctx = useContext(EventHandlingContext);
  if (!ctx) {
    throw new Error(
      "useEventHandlingContext must be used within an EventHandlingProvider",
    );
  }
  return ctx;
};

export const useOptionalEventHandlingContext = ():
  | EventHandlingContextValue
  | undefined => useContext(EventHandlingContext);
